import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard,
  Inbox,
  FolderTree,
  ListFilter,
  Contact,
  Settings as SettingsIcon,
  LogOut,
  Building2,
  FileType,
  Bell,
  Check,
  X,
  CheckCheck,
  Share2,
  ShieldAlert,
} from "lucide-react";

const navItems = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/sas", label: "SAS — Dépôt", icon: Inbox, testid: "nav-sas" },
  { to: "/queue", label: "À classer", icon: ListFilter, testid: "nav-queue" },
  { to: "/docs", label: "Documents", icon: FolderTree, testid: "nav-tree" },
  { to: "/types", label: "Types de documents", icon: FileType, testid: "nav-types" },
  { to: "/contacts", label: "Contacts", icon: Contact, testid: "nav-contacts" },
  { to: "/partages", label: "Partages", icon: Share2, testid: "nav-shares" },
];

const TYPE_LABELS = {
  document_type_proposal: "Proposition",
  document_type_approved:  "Approuvé",
  document_type_rejected:  "Refusé",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const orgName = user?.org_name || "—";

  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnreadCount(data.count || 0);
    } catch (_) {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.read).length);
    } catch (_) {}
  }, []);

  // Poll every 30 s
  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  // Open → fetch full list
  useEffect(() => {
    if (notifOpen) fetchNotifications();
  }, [notifOpen, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen]);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "À l'instant";
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  return (
    <div className="min-h-screen flex bg-[#fdfdfd] text-zinc-900">
      <aside className="w-[260px] shrink-0 border-r border-zinc-200 flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-zinc-200">
          <div className="flex items-baseline gap-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-mono">BeDocs</div>
            <div className="h-1 w-1 bg-[#FF3B30]" />
          </div>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">Gestion Documentaire</h1>
          <div
            className="mt-2 flex items-center gap-1.5 bg-zinc-100 border border-zinc-200 px-2 py-1"
            data-testid="org-badge"
          >
            <Building2 size={11} strokeWidth={1.5} className="text-zinc-500 shrink-0" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-mono truncate">
              {orgName}
            </span>
          </div>
        </div>

        <nav className="flex-1 overflow-auto app-scroll py-4">
          <div className="px-4 mb-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
            Espace
          </div>
          <ul>
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                      isActive
                        ? "border-[#002FA7] bg-zinc-100 text-zinc-900 font-medium"
                        : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                    }`
                  }
                >
                  <item.icon size={16} strokeWidth={1.5} />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          {isAdmin && (
            <>
              <div className="px-4 mt-6 mb-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
                Administration
              </div>
              <ul>
                <li>
                  <NavLink
                    to="/parametres"
                    data-testid="nav-settings"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                        isActive
                          ? "border-[#002FA7] bg-zinc-100 text-zinc-900 font-medium"
                          : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                      }`
                    }
                  >
                    <SettingsIcon size={16} strokeWidth={1.5} />
                    Paramètres
                  </NavLink>
                </li>
                {isSuperAdmin && (
                  <li>
                    <NavLink
                      to="/administration/logs"
                      data-testid="nav-admin-logs"
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                          isActive
                            ? "border-[#DC2626] bg-red-50 text-red-700 font-medium"
                            : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                        }`
                      }
                    >
                      <ShieldAlert size={16} strokeWidth={1.5} />
                      Administration
                    </NavLink>
                  </li>
                )}
              </ul>
            </>
          )}
        </nav>

        {/* ── Bottom: Notifications + User ── */}
        <div className="border-t border-zinc-200 p-4">
          {/* Notification bell row */}
          <div className="flex items-center justify-between mb-3" ref={panelRef}>
            <div className="relative">
              <button
                data-testid="notif-bell-btn"
                onClick={() => setNotifOpen((o) => !o)}
                className="relative p-2 text-zinc-500 hover:text-zinc-900 transition-colors"
                title="Notifications"
              >
                <Bell
                  size={16}
                  strokeWidth={1.5}
                  className={unreadCount > 0 ? "bell-ring text-[#002FA7]" : ""}
                />
                {unreadCount > 0 && (
                  <span
                    data-testid="notif-badge"
                    className="absolute top-1 right-1 min-w-[14px] h-[14px] bg-[#FF3B30] text-white text-[9px] font-bold font-mono flex items-center justify-center px-0.5 leading-none"
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications dropdown */}
              {notifOpen && (
                <div
                  className="absolute bottom-full left-0 mb-2 w-[320px] bg-white border border-zinc-200 shadow-xl z-50 animate-fade-in-up"
                  data-testid="notif-panel"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
                    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-700 font-mono">
                      Notifications
                    </span>
                    <div className="flex items-center gap-1">
                      {unreadCount > 0 && (
                        <button
                          data-testid="notif-mark-all-read"
                          onClick={markAllRead}
                          title="Tout marquer comme lu"
                          className="p-1 text-zinc-400 hover:text-[#002FA7] transition-colors"
                        >
                          <CheckCheck size={14} strokeWidth={1.5} />
                        </button>
                      )}
                      <button
                        onClick={() => setNotifOpen(false)}
                        className="p-1 text-zinc-400 hover:text-zinc-700 transition-colors"
                      >
                        <X size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>

                  {/* List */}
                  <div className="max-h-72 overflow-y-auto divide-y divide-zinc-100">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-xs text-zinc-400">
                        Aucune notification
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          data-testid={`notif-item-${n.id}`}
                          className={`px-4 py-3 flex gap-3 transition-colors ${
                            !n.read ? "bg-blue-50/60 hover:bg-blue-50" : "hover:bg-zinc-50"
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className="text-[10px] font-mono text-zinc-400 uppercase tracking-wide">
                                {TYPE_LABELS[n.type] || n.type}
                              </span>
                              {!n.read && n.type !== "document_type_proposal" && (
                                <span className="w-1.5 h-1.5 bg-[#002FA7] shrink-0" />
                              )}
                              {n.type === "document_type_proposal" && (
                                <span className="text-[9px] font-mono px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-700">
                                  À traiter
                                </span>
                              )}
                            </div>
                            <div className="text-xs font-medium text-zinc-900 leading-snug">{n.title}</div>
                            <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{n.message}</div>
                            <div className="text-[10px] text-zinc-400 font-mono mt-1">{formatTime(n.created_at)}</div>
                          </div>
                          {/* Only non-proposal notifications can be manually dismissed */}
                          {!n.read && n.type !== "document_type_proposal" && (
                            <button
                              onClick={() => markRead(n.id)}
                              title="Marquer comme lu"
                              className="shrink-0 p-1 text-zinc-300 hover:text-emerald-500 transition-colors mt-0.5"
                            >
                              <Check size={13} strokeWidth={2} />
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* User info */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">
                {user?.name || user?.email}
              </div>
              <div className="text-xs text-zinc-500 font-mono truncate">{user?.email}</div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-[#002FA7] font-mono">
                {user?.role === "admin" ? "Administrateur" : user?.role === "super_admin" ? "Super Admin" : "Utilisateur"}
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="p-2 text-zinc-500 hover:text-[#FF3B30] transition-colors"
              title="Se déconnecter"
            >
              <LogOut size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
